/**
 * HierarchyContext.js
 *
 * Provides a three-level hierarchical model for trust-center assessments:
 *   Enterprise  → full organisation scope (no filtering)
 *   Department  → filtered to a specific department
 *   Discipline  → filtered to a specific focus-area / discipline within a department
 *
 * Access control matrix:
 *   admin / assessmentadmin   → all levels, all departments, all disciplines
 *   department_admin          → Department + Discipline levels for their own dept only
 *   reviewer / user           → read-only at Enterprise level (shared assessments)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { assessmentsApi } from '../services/api';

// ─── Constants ───────────────────────────────────────────────────────────────

export const HIERARCHY_LEVELS = {
  ENTERPRISE: 'enterprise',
  DEPARTMENT: 'department',
  DISCIPLINE: 'discipline',
};

export const LEVEL_META = {
  enterprise: {
    label: 'Enterprise',
    description: 'Full organisational scope — all departments and disciplines',
    color: 'indigo',
    order: 0,
  },
  department: {
    label: 'Department',
    description: 'Scoped to a single business department',
    color: 'sky',
    order: 1,
  },
  discipline: {
    label: 'Discipline',
    description: 'Scoped to a specific practice area or focus discipline',
    color: 'teal',
    order: 2,
  },
};

/** Roles that may operate at Enterprise level (see everything). */
const ENTERPRISE_ROLES = ['admin', 'assessmentadmin'];

/** Roles that are limited to their own department. */
const DEPARTMENT_ROLES = ['department_admin', 'departmentadmin'];

// ─── Context ─────────────────────────────────────────────────────────────────

const HierarchyContext = createContext(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export const HierarchyProvider = ({ children }) => {
  const { user } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────

  const [hierarchyLevel, setHierarchyLevelState] = useState(
    HIERARCHY_LEVELS.ENTERPRISE,
  );
  const [selectedDepartment, setSelectedDepartmentState] = useState(null);
  const [selectedDiscipline, setSelectedDisciplineState] = useState(null);

  // Lookup data
  const [departments, setDepartments] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  // ── Derived role info ──────────────────────────────────────────────────────

  const userRole = user?.role || '';
  const userRoles = useMemo(
    () => (Array.isArray(user?.roles) ? user.roles : []),
    [user?.roles],
  );

  const isEnterpriseAdmin = useMemo(
    () => {
      // A user with a department-scoped role is NEVER enterprise admin,
      // even if they also carry the 'admin' dashboard role (which the
      // RiskWebApp shim sets so they land on admin pages).
      const isDept =
        DEPARTMENT_ROLES.some(r => r === userRole || userRoles.includes(r));
      if (isDept) return false;
      return ENTERPRISE_ROLES.some(r => r === userRole || userRoles.includes(r));
    },
    [userRole, userRoles],
  );

  const isDeptAdmin = useMemo(
    () =>
      !isEnterpriseAdmin &&
      DEPARTMENT_ROLES.some(r => r === userRole || userRoles.includes(r)),
    [isEnterpriseAdmin, userRole, userRoles],
  );

  /** The department the logged-in department_admin belongs to (if applicable). */
  const ownDepartment = useMemo(() => {
    if (!isDeptAdmin) return null;
    return (user?.department || '').trim() || null;
  }, [isDeptAdmin, user?.department]);

  // ── Generic restriction for any non-enterprise role ────────────────────────
  // Per least-privilege: reviewers, trustcenter users, dept admins, etc. only
  // see assessments matching their own department (and discipline, if set).
  // If the user has no department configured → they see nothing.
  const isRestricted = !isEnterpriseAdmin;

  const userOwnDept = useMemo(
    () => (isRestricted ? (user?.department || '').trim() : ''),
    [isRestricted, user?.department],
  );

  const userOwnDiscipline = useMemo(
    () => (isRestricted ? (user?.discipline || '').trim() : ''),
    [isRestricted, user?.discipline],
  );

  // ── Load lookups once ──────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    const loadLookups = async () => {
      setLookupsLoading(true);
      try {
        const [deptsRes, disciplinesRes] = await Promise.all([
          assessmentsApi.getDepartments(),
          assessmentsApi.getDisciplines(),
        ]);
        if (!cancelled) {
          setDepartments(deptsRes || []);
          setDisciplines(disciplinesRes || []);
        }
      } catch (err) {
        console.error('[HierarchyContext] Failed to load lookups:', err);
      } finally {
        if (!cancelled) setLookupsLoading(false);
      }
    };

    if (user) loadLookups();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // ── Constrain initial level for dept admins ────────────────────────────────

  useEffect(() => {
    if (isDeptAdmin && ownDepartment) {
      setHierarchyLevelState(HIERARCHY_LEVELS.DEPARTMENT);
      setSelectedDepartmentState(ownDepartment);
    }
  }, [isDeptAdmin, ownDepartment]);

  // ── Setters with access guard ──────────────────────────────────────────────

  const setHierarchyLevel = useCallback(
    (level) => {
      if (!canViewLevel(level)) return;
      setHierarchyLevelState(level);
      // Reset deeper selections when moving to a higher level
      if (level === HIERARCHY_LEVELS.ENTERPRISE) {
        if (!isDeptAdmin) setSelectedDepartmentState(null);
        setSelectedDisciplineState(null);
      }
      if (level === HIERARCHY_LEVELS.DEPARTMENT) {
        setSelectedDisciplineState(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDeptAdmin],
  );

  const setSelectedDepartment = useCallback(
    (dept) => {
      // dept admins can only select their own department
      if (isDeptAdmin && dept && dept !== ownDepartment) return;
      setSelectedDepartmentState(dept);
      setSelectedDisciplineState(null); // reset discipline when dept changes
    },
    [isDeptAdmin, ownDepartment],
  );

  const setSelectedDiscipline = useCallback((disc) => {
    setSelectedDisciplineState(disc);
  }, []);

  // ── Access helpers ─────────────────────────────────────────────────────────

  const canViewLevel = useCallback(
    (level) => {
      if (isEnterpriseAdmin) return true;
      if (isDeptAdmin) {
        return (
          level === HIERARCHY_LEVELS.DEPARTMENT ||
          level === HIERARCHY_LEVELS.DISCIPLINE
        );
      }
      // read-only roles can only view enterprise
      return level === HIERARCHY_LEVELS.ENTERPRISE;
    },
    [isEnterpriseAdmin, isDeptAdmin],
  );

  /**
   * Returns true if the current user can manage (create/edit) assessments
   * at the given hierarchy level.
   */
  const canManageLevel = useCallback(
    (level) => {
      if (isEnterpriseAdmin) return true;
      if (isDeptAdmin) {
        return (
          level === HIERARCHY_LEVELS.DEPARTMENT ||
          level === HIERARCHY_LEVELS.DISCIPLINE
        );
      }
      return false;
    },
    [isEnterpriseAdmin, isDeptAdmin],
  );

  // ── Filtering ──────────────────────────────────────────────────────────────

  /**
   * Filters a flat assessments array to only those visible at the current
   * hierarchy scope.  Filters are applied in order: dept → discipline.
   *
   * @param {Array} assessments - Raw assessment list
   * @returns {Array} Assessments visible at the current scope
   */
  const filterAssessmentsByScope = useCallback(
    (assessments) => {
      if (!Array.isArray(assessments)) return [];

      /** Get the department label from an assessment, trying multiple field names */
      const getDept = (a) =>
        (a.departmentName || a.departmentDesc || '').trim().toLowerCase();

      /** Get the discipline label from an assessment */
      const getDisc = (a) =>
        (a.disciplineName || a.disciplineDesc || '').trim().toLowerCase();

      // ── Least-privilege gate for any non-enterprise admin role ─────────
      // Reviewers / trustcenter users / dept admins must have a department
      // configured; otherwise they see nothing (principle of least privilege).
      if (isRestricted) {
        if (!userOwnDept) return [];
        const deptLc = userOwnDept.toLowerCase();
        // A department admin owns the WHOLE department (all disciplines within it), so their
        // own discipline must not narrow the results. Only genuinely discipline-scoped roles
        // (e.g. reviewers) are constrained by discipline.
        const discLc = isDeptAdmin ? '' : userOwnDiscipline.toLowerCase();
        return assessments.filter(a => {
          if (getDept(a) !== deptLc) return false;
          if (discLc && getDisc(a) !== discLc) return false;
          return true;
        });
      }

      // ── Enterprise admin: respect the chosen hierarchy scope ───────────
      if (hierarchyLevel === HIERARCHY_LEVELS.ENTERPRISE) {
        return assessments;
      }

      if (hierarchyLevel === HIERARCHY_LEVELS.DEPARTMENT) {
        const deptFilter = (selectedDepartment || '').trim().toLowerCase();
        if (!deptFilter) return assessments;
        return assessments.filter(a => getDept(a) === deptFilter);
      }

      if (hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE) {
        const deptFilter = (selectedDepartment || '').trim().toLowerCase();
        const discFilter = (selectedDiscipline || '').trim().toLowerCase();
        let filtered = assessments;
        if (deptFilter) filtered = filtered.filter(a => getDept(a) === deptFilter);
        if (discFilter) filtered = filtered.filter(a => getDisc(a) === discFilter);
        return filtered;
      }

      return assessments;
    },
    [
      hierarchyLevel,
      isRestricted,
      isDeptAdmin,
      userOwnDept,
      userOwnDiscipline,
      selectedDepartment,
      selectedDiscipline,
    ],
  );

  // ── Scope label ────────────────────────────────────────────────────────────

  const scopeLabel = useMemo(() => {
    const levelMeta = LEVEL_META[hierarchyLevel];
    if (hierarchyLevel === HIERARCHY_LEVELS.ENTERPRISE) {
      return `Enterprise — All Departments`;
    }
    if (hierarchyLevel === HIERARCHY_LEVELS.DEPARTMENT) {
      const dept = isDeptAdmin ? ownDepartment : selectedDepartment;
      return dept
        ? `Department — ${dept}`
        : `${levelMeta.label} — Select a Department`;
    }
    if (hierarchyLevel === HIERARCHY_LEVELS.DISCIPLINE) {
      const dept = isDeptAdmin ? ownDepartment : selectedDepartment;
      const parts = [dept, selectedDiscipline].filter(Boolean);
      return parts.length
        ? `Discipline — ${parts.join(' › ')}`
        : `${levelMeta.label} — Select Scope`;
    }
    return levelMeta.label;
  }, [
    hierarchyLevel,
    isDeptAdmin,
    ownDepartment,
    selectedDepartment,
    selectedDiscipline,
  ]);

  // ── Disciplines scoped to the selected department ──────────────────────────

  const disciplinesForDepartment = useMemo(() => {
    const deptFilter = isDeptAdmin ? ownDepartment : selectedDepartment;
    if (!deptFilter || !Array.isArray(disciplines)) return disciplines ?? [];
    const lc = deptFilter.trim().toLowerCase();
    const filtered = disciplines.filter(
      d =>
        !d.departmentDesc ||
        d.departmentDesc.trim().toLowerCase() === lc,
    );
    return filtered.length > 0 ? filtered : disciplines;
  }, [disciplines, isDeptAdmin, ownDepartment, selectedDepartment]);

  // ── Context value ──────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      // State
      hierarchyLevel,
      selectedDepartment: isDeptAdmin ? ownDepartment : selectedDepartment,
      selectedDiscipline,
      scopeLabel,

      // Lookups
      departments,
      disciplines,
      disciplinesForDepartment,
      lookupsLoading,

      // Role flags
      isEnterpriseAdmin,
      isDeptAdmin,
      isRestricted,
      ownDepartment,
      userOwnDept,
      userOwnDiscipline,

      // Setters
      setHierarchyLevel,
      setSelectedDepartment,
      setSelectedDiscipline,

      // Helpers
      canViewLevel,
      canManageLevel,
      filterAssessmentsByScope,

      // Constants
      HIERARCHY_LEVELS,
      LEVEL_META,
    }),
    [
      hierarchyLevel,
      selectedDepartment,
      selectedDiscipline,
      scopeLabel,
      departments,
      disciplines,
      disciplinesForDepartment,
      lookupsLoading,
      isEnterpriseAdmin,
      isDeptAdmin,
      isRestricted,
      ownDepartment,
      userOwnDept,
      userOwnDiscipline,
      setHierarchyLevel,
      setSelectedDepartment,
      setSelectedDiscipline,
      canViewLevel,
      canManageLevel,
      filterAssessmentsByScope,
    ],
  );

  return (
    <HierarchyContext.Provider value={value}>
      {children}
    </HierarchyContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useHierarchy = () => {
  const ctx = useContext(HierarchyContext);
  if (!ctx) {
    throw new Error('useHierarchy must be used within a HierarchyProvider');
  }
  return ctx;
};

export default HierarchyContext;
